import { v4 as uuid } from 'uuid';
import invariant from 'invariant';
import { showNotification } from 'actions/notifications-actions';
import graphql from 'api/graphql';
import { isPopoutPlayer } from 'globalConst/const';
import { playbackModes } from 'store/reducers/player';
import { NotificationTypes, NotificationTexts } from '../components/Notifications/Notification';
import AudioPlayerStation from '../players/AudioPlayerStation';
import AudioPlayerOnDemand from '../players/AudioPlayerOnDemand';
import VideoJWPlayer from '../players/VideoJWPlayer';
import ChromeSender from '../players/ChromeSender';
import { setMuted, setVolumeLevel } from './playerVolume-actions';
import { trackHealthyPlayStation, trackHealthyPlayOnDemand } from './healthyPlay-actions';
import { inlineMediaStop } from './inline-media-actions';
import { playerActions } from './actions-const';
import { GIGYA_SCREENSET } from 'globalConst/gigya-const';
import { castingStop, castingStart, castingPlaybackError } from './casting-actions';
import { setUserHDPreference, showTalpaGigyaModal } from './gigya-actions';
import * as huaweiPlayerEvents from 'players/HuaweiPlayerEvents';
import { reportFailedRadioStreaming } from 'metrics';

import {
  setPlaybackDurationInPopout,
  setStationInPopout,
  setClipInPopout,
  setPlaybackTimeInPopout,
} from './popout-actions';
import { VIDEO_PLAYER_ID } from '../players/Players.const';
import { logger } from 'util/index';

export const PLAYBACK_TYPE = {
  LIVE_AUDIO: 'liveAudio',
  ONDEMAND: 'ondemand',
  VIDEO: 'video',
};

export function playerStop() {
  return {
    type: playerActions.PLAYER_STOPPED,
  };
}

export function playerPlaying() {
  return {
    type: playerActions.PLAYER_PLAYING,
  };
}

export function setPlaybackToLiveAudio() {
  return {
    type: playerActions.PLAYER_SET_PLAYBACK_TO_LIVE_AUDIO,
  };
}

export function setPlaybackToLiveVideo() {
  return {
    type: playerActions.PLAYER_SET_PLAYBACK_TO_LIVE_VIDEO,
  };
}

export function setPlaybackToOnDemandClip() {
  return {
    type: playerActions.PLAYER_SET_PLAYBACK_TO_ON_DEMAND_CLIP,
  };
}

export function setUpcomingTracks(tracks) {
  return {
    type: playerActions.PLAYER_SET_UPCOMING_TRACKS,
    tracks,
  };
}

export function skipOnDemandClipForward(skipTime) {
  AudioPlayerOnDemand.skipForward(skipTime);
  ChromeSender.skipForward(skipTime);
  huaweiPlayerEvents.message('skipForward', skipTime);
  return {
    type: playerActions.PLAYER_SKIP_ON_DEMAND_CLIP_FORWARD,
    skipTime,
  };
}

export function skipOnDemandClipBackward(skipTime) {
  AudioPlayerOnDemand.skipBackward(skipTime);
  ChromeSender.skipBackward(skipTime);
  huaweiPlayerEvents.message('skipBackward', skipTime);
  return {
    type: playerActions.PLAYER_SKIP_ON_DEMAND_CLIP_BACKWARD,
    skipTime,
  };
}

export function setOnDemandClipTime(newTime) {
  AudioPlayerOnDemand.skipTo(newTime);
  ChromeSender.skipTo(newTime);
  return {
    type: playerActions.PLAYER_SET_ON_DEMAND_TIME,
    newTime,
  };
}

export function trackOnDemandPlaybackTime(onDemandPlaybackTime) {
  return async (dispatch) => {
    if (isPopoutPlayer) {
      dispatch(setPlaybackTimeInPopout(onDemandPlaybackTime));
    }
    dispatch({
      type: playerActions.PLAYER_TRACK_ON_DEMAND_PLAYBACK_TIME,
      onDemandPlaybackTime,
    });
  };
}

export function emptyOnDemandPlayback() {
  return {
    type: playerActions.PLAYER_EMPTY_ON_DEMAND_PLAYBACK,
  };
}

export function setOnDemandPlaybackDuration(onDemandPlaybackDuration) {
  return async (dispatch) => {
    if (isPopoutPlayer) {
      dispatch(setPlaybackDurationInPopout(onDemandPlaybackDuration));
    }
    dispatch({
      type: playerActions.PLAYER_SET_ON_DEMAND_PLAYBACK_DURATION,
      onDemandPlaybackDuration,
    });
  };
}

export function setFullPlayerState(visible) {
  return {
    type: playerActions.PLAYER_SET_FULL_PLAYER,
    visible,
  };
}

export function forceMiniPlayerState(visible) {
  return {
    type: playerActions.PLAYER_FORCE_MINI_PLAYER,
    visible,
  };
}

export function hideMiniPlayer(payload) {
  return {
    type: playerActions.PLAYER_HIDE_MINI_PLAYER,
    payload,
  };
}

export function hidePlayer() {
  return async (dispatch) => {
    dispatch({
      type: playerActions.PLAYER_HIDE_PLAYER,
    });
  };
}

export function setPlayerType(payload) {
  return {
    type: playerActions.SET_PLAYER_TYPE,
    payload,
  };
}

export function castingAvailable() {
  return {
    type: playerActions.CASTING_AVAILABLE,
  };
}

export function castingUnavailable() {
  return {
    type: playerActions.CASTING_UNAVAILABLE,
  };
}

export function playbackError(type, error, mediaItem, playSessionId) {
  //defining feature flag like this is temporary solution until we have new A/B testing tool
  const splunk_error_reporting = true;
  return (dispatch) => {
    const { name } = error || {};
    if (name === 'NotAllowedError' || name === 'AbortError') {
      dispatch(playerStop());
      return;
    }
    if (splunk_error_reporting) {
      reportFailedRadioStreaming(`playback error/${error.message}`, mediaItem.name, mediaItem.uuid);
    }

    const { title } = mediaItem || {};
    let message = null;
    switch (type) {
      case PLAYBACK_TYPE.ONDEMAND:
        message = NotificationTexts.PLAYBACK_CLIP_ERROR;
        logger.error(`${playSessionId} AudioPlayerStation.playOnDemandClip errored: ${error.message}`);
        break;
      case PLAYBACK_TYPE.VIDEO:
        message = `${title} ${NotificationTexts.PLAYBACK_LIVE_VIDEO_ERROR}`;
        logger.error(`${playSessionId} playLiveVideo.play errored: ${error.message}`);
        break;
      case PLAYBACK_TYPE.LIVE_AUDIO:
        message = `${title} ${NotificationTexts.PLAYBACK_STATION_ERROR}`;
        logger.error(`${playSessionId} playLiveAudio.play errored: ${error.message}`);
        break;
      default:
        break;
    }

    if (name === 'MixedContent') {
      message = `${
        title || 'Deze radiozender of podcast'
      } is tijdelijk niet beschikbaar in Google Chrome. Probeer een andere browser.`;
    }

    dispatch({ type: playerActions.PLAYER_ERRORED, playSessionId, error });
    dispatch(
      showNotification({
        type: NotificationTypes.ERROR,
        message,
      })
    );
  };
}

export const setTrackOnDemandPlaybackTime = (onDemandPlaybackTime) => (dispatch, getState) => {
  const {
    player: { playSessionId },
  } = getState();

  invariant(playSessionId, 'playSessionId should be set before calling setTrackOnDemandPlaybackTime()');

  dispatch({
    type: playerActions.PLAYER_SET_ON_DEMAND_PLAYBACK_TIME,
    onDemandPlaybackTime,
  });

  try {
    AudioPlayerOnDemand.skipTo(onDemandPlaybackTime);
    ChromeSender.skipTo(onDemandPlaybackTime);
  } catch (error) {
    logger.error(playSessionId, 'AudioPlayerOnDemand.play errored', error);
    dispatch({ type: playerActions.PLAYER_ERRORED, playSessionId, error });
  }
};

export function attemptAutoPlayNext() {
  return async (dispatch, getState) => {
    const {
      player: { upcomingTracks },
    } = getState();
    if (!upcomingTracks || !upcomingTracks.length) return;

    const [nextTrack, ...tracks] = upcomingTracks;
    // eslint-disable-next-line no-use-before-define
    dispatch(loadOnDemandClip(nextTrack));
    dispatch(setUpcomingTracks(tracks));
  };
}

export function initPlayer() {
  return async (dispatch) => {
    //ONLY ON HUAWEI DEVICES: Listen to the native audio player
    huaweiPlayerEvents.listener();

    AudioPlayerStation.trackHealthyPlay((playSessionId) => {
      dispatch(trackHealthyPlayStation(playSessionId));
    });

    AudioPlayerOnDemand.trackHealthyPlay((playSessionId) => {
      dispatch(trackHealthyPlayOnDemand(playSessionId));
    });

    VideoJWPlayer.trackHealthyPlay(() => {
      dispatch(() => {
        // TODO JWPlayer Healthy play callback VideoJWPlayer.trackHealthyPlay((playSessionId)
      });
    });

    AudioPlayerStation.endedPlay(() => {
      dispatch(playerStop());
    });

    AudioPlayerOnDemand.endedPlay(() => {
      dispatch(playerStop());
      dispatch(attemptAutoPlayNext());
    });

    AudioPlayerOnDemand.timeUpdate((playbackTime) => {
      dispatch(trackOnDemandPlaybackTime(playbackTime));
    });

    AudioPlayerOnDemand.emptied(() => {
      dispatch(emptyOnDemandPlayback());
    });

    AudioPlayerOnDemand.durationChanged((onDemandDuration) => {
      dispatch(setOnDemandPlaybackDuration(onDemandDuration));
    });

    ChromeSender.setCastingAvailableCallback(() => dispatch(castingAvailable()));
    ChromeSender.setCastingUnavailableCallback(() => dispatch(castingUnavailable()));
    ChromeSender.setCastingStopCallback(() => dispatch(castingStop()));
    ChromeSender.setCastingStartCallback((device) => dispatch(castingStart(device)));
    ChromeSender.setPlayerPlayingCallback(() => dispatch(playerPlaying()));
    ChromeSender.setSetMutedCallback((isMuted) => dispatch(setMuted(isMuted)));
    ChromeSender.setSetVolumeLevelCallback((volume) => dispatch(setVolumeLevel(volume)));
    ChromeSender.setEndedPlayCallback(() => dispatch(playerStop()));
    ChromeSender.setTimeUpdateCallback((playbackTime) => dispatch(trackOnDemandPlaybackTime(playbackTime)));
    ChromeSender.setDurationChangedCallback((onDemandDuration) =>
      dispatch(setOnDemandPlaybackDuration(onDemandDuration))
    );
    ChromeSender.setErrorCallback((message) => dispatch(castingPlaybackError(message)));

    AudioPlayerOnDemand.setStopCallback(() => dispatch(playerStop()));
    AudioPlayerOnDemand.setPlayCallback(() => dispatch(playerPlaying()));
    AudioPlayerOnDemand.setErrorCallback((error, ondemandItem, playSessionId) =>
      dispatch(playbackError(PLAYBACK_TYPE.ONDEMAND, error, ondemandItem, playSessionId))
    );
    AudioPlayerOnDemand.setProgressSentCallback(() =>
      dispatch({
        type: playerActions.PROGRESS_UPDATE,
      })
    );

    AudioPlayerStation.setStopCallback(() => dispatch(playerStop()));
    AudioPlayerStation.setPlayCallback(() => dispatch(playerPlaying()));
    AudioPlayerStation.setErrorCallback((error, station, playSessionId) =>
      dispatch(playbackError(PLAYBACK_TYPE.LIVE_AUDIO, error, station, playSessionId))
    );

    VideoJWPlayer.setErrorCallback((station) => dispatch(playbackError(PLAYBACK_TYPE.VIDEO, null, station)));
    VideoJWPlayer.setVolumeCallback((volume) => dispatch(setVolumeLevel(volume)));
    VideoJWPlayer.setMuteCallback((mute) => dispatch(setMuted(mute)));
    VideoJWPlayer.setPlayCallback(() => dispatch(playerPlaying()));
    VideoJWPlayer.setStopCallback(() => dispatch(playerStop()));
    dispatch({
      type: playerActions.PLAYER_INITIALIZED,
    });
  };
}

export function playOnDemandClip() {
  return async (dispatch, getState) => {
    const {
      player: { playSessionId, playbackMode, currentOnDemandClip, isCasting, onDemandPlaybackTime, onDemandDuration },
      popout: { isPopoutPlayerActive },
      user: { isLoggedIn },
    } = getState();

    invariant(currentOnDemandClip, 'currentOnDemandClip should be set before calling playOnDemandClip()');
    invariant(playSessionId, 'playSessionId should be set before calling playOnDemandClip()');

    // if popout player is active, only start playing in the popout layer
    if (isPopoutPlayerActive && !isPopoutPlayer) {
      dispatch(setClipInPopout(currentOnDemandClip));
      return;
    }

    try {
      dispatch({ type: playerActions.PLAYER_LOADING, playSessionId });
      AudioPlayerStation.stop(false);
      VideoJWPlayer.stop(false);
      dispatch(inlineMediaStop());

      if (isCasting) {
        await ChromeSender.play({
          playSessionId,
          currentOnDemandClip,
          onDemandPlaybackTime,
          onDemandDuration,
          playbackMode,
        });
      } else {
        AudioPlayerOnDemand.play(playSessionId, currentOnDemandClip, onDemandPlaybackTime, isLoggedIn);
        huaweiPlayerEvents.message('play', currentOnDemandClip);
      }
      dispatch(setMuted(false, false));
    } catch (error) {
      dispatch(playbackError(PLAYBACK_TYPE.ONDEMAND, error, playSessionId));
    }
  };
}

export function playLiveAudio() {
  return async (dispatch, getState) => {
    const {
      player: { playSessionId, currentStation, isCasting, playbackMode },
      user: { attemptingLogin, isLoading, isLoggedIn, prefersHDSources },
      popout: { isPopoutPlayerActive },
    } = getState();

    invariant(currentStation, 'currentStation should be set before calling playLiveAudio()');

    // if popout player is active, only start playing in the popout layer
    if (isPopoutPlayerActive && !isPopoutPlayer) {
      dispatch(setStationInPopout(currentStation, playbackModes.LIVE_AUDIO));
      return;
    }

    /*
      NOTE The player has no exception management so when an error is thrown specifc
      to a situation (eg. not logged in user), all the player can do is fail with no
      other flow proposed. There is an opportunity to solve this here by checking
      isLoggedIn and prefersHDSources
    */
    let useHD = !!currentStation.tritonMountHD && prefersHDSources && isLoggedIn;
    if (prefersHDSources && !isLoggedIn && !isLoading) {
      if (attemptingLogin) {
        // User previously canceled login modal
        useHD = false;
        dispatch(setUserHDPreference(false));
      } else {
        // User needs to login, show login screenset
        dispatch(showTalpaGigyaModal(GIGYA_SCREENSET.REGISTRATION_LOGIN, GIGYA_SCREENSET.LOGIN_SCREEN));
        return;
      }
    }

    dispatch({ type: playerActions.PLAYER_USE_HD, useHD });

    invariant(playSessionId, 'playSessionId should be set before calling playLiveAudio()');

    AudioPlayerOnDemand.stop(false);
    VideoJWPlayer.stop(false);

    try {
      dispatch({ type: playerActions.PLAYER_LOADING, playSessionId });
      dispatch(inlineMediaStop());

      if (isCasting) {
        await ChromeSender.play({
          playSessionId,
          currentStation,
          playbackMode,
        });
      } else {
        await AudioPlayerStation.play(playSessionId, currentStation, useHD);
        huaweiPlayerEvents.message('play', currentStation);
      }
      dispatch(setMuted(false, false));
      dispatch(playerPlaying());

      // Show HD
      if (useHD && currentStation.tritonMountHD) {
        dispatch(
          showNotification({
            type: NotificationTypes.NOTIFICATION,
            message: useHD ? NotificationTexts.HD_ENABLE : NotificationTexts.HD_DISABLE,
          })
        );
      }
    } catch (error) {
      if (!isCasting) dispatch(playbackError(PLAYBACK_TYPE.LIVE_AUDIO, error, currentStation, playSessionId));
    }
  };
}

export function playLiveVideo() {
  return async (dispatch, getState) => {
    const {
      player: { playSessionId, currentStation, isCasting, playbackMode },
    } = getState();

    invariant(currentStation, 'currentStation should be set before calling playLiveVideo()');
    invariant(playSessionId, 'playSessionId should be set before calling playLiveVideo()');

    try {
      dispatch({ type: playerActions.PLAYER_LOADING, playSessionId });
      AudioPlayerOnDemand.stop(false);
      AudioPlayerStation.stop(false);
      dispatch(inlineMediaStop());

      if (isCasting) {
        await ChromeSender.play({
          playSessionId,
          currentStation,
          playbackMode,
        });
      } else {
        await VideoJWPlayer.play(playSessionId, currentStation);
      }

      dispatch(setMuted(false, false));
      dispatch(playerPlaying());
    } catch (error) {
      dispatch(playbackError(PLAYBACK_TYPE.VIDEO, error, currentStation, playSessionId));
    }
  };
}

export function loadOnDemandClip(newonDemandClip, autoPlay = true) {
  return async (dispatch, getState) => {
    const {
      player: { currentOnDemandClip, isPlaying },
    } = getState();

    dispatch(setPlaybackToOnDemandClip());

    if (currentOnDemandClip && currentOnDemandClip.id === newonDemandClip.id) {
      if (!isPlaying && autoPlay) {
        dispatch(playOnDemandClip());
      }
    } else {
      dispatch({
        type: playerActions.PLAYER_LOAD_ON_DEMAND_CLIP,
        onDemandClip: newonDemandClip,
        playSessionId: uuid(),
      });

      if (autoPlay) {
        dispatch(playOnDemandClip());
      }
    }
  };
}

export function loadLiveVideo(station, autoPlay = true) {
  return async (dispatch, getState) => {
    const {
      player: { currentStation, isPlaying, playbackMode },
    } = getState();

    await dispatch(setPlaybackToLiveVideo());

    if (currentStation && currentStation.slug === station.slug) {
      // Start playing the current live video if it was stopped,
      // or play when switching from the live audio mode
      if ((!isPlaying || playbackMode !== playbackMode.LIVE_AUDIO) && autoPlay) {
        if (playbackMode !== playbackModes.LIVE_VIDEO) {
          const playSessionId = uuid();

          dispatch({
            type: playerActions.PLAYER_LOAD_LIVE_VIDEO_STATION,
            playSessionId,
            station,
          });
        }
        dispatch(playLiveVideo());
      }
    } else {
      // Starting a new playback session
      const playSessionId = uuid();
      dispatch({
        type: playerActions.PLAYER_LOAD_LIVE_VIDEO_STATION,
        playSessionId,
        station,
      });

      if (autoPlay) {
        dispatch(playLiveVideo());
      }
    }
  };
}

export function loadStation(station, autoPlay = true) {
  return async (dispatch, getState) => {
    const {
      player: { currentStation, isPlaying, playbackMode },
      user: { prefersHDSources },
    } = getState();

    if (playbackMode === playbackModes.LIVE_VIDEO && document.getElementById(VIDEO_PLAYER_ID) && station.videoUrl) {
      dispatch(loadLiveVideo(station, autoPlay));
      return;
    }

    dispatch(setPlaybackToLiveAudio());
    if (
      currentStation?.slug === station.slug &&
      //Collection title can be updated when a experiment between collections is active.
      //In that case a PLAYER_LOAD_STATION needs to be dispatched to update the collectiontitle of the currentStation item in the store
      currentStation?.collectionTitle === station.collectionTitle &&
      !prefersHDSources
    ) {
      // Start playing the current live audio if it was stopped,
      // or play when switching from the live video mode
      if ((!isPlaying || playbackMode !== playbackMode.LIVE_VIDEO) && autoPlay) {
        dispatch(playLiveAudio());
      }
    } else {
      const playSessionId = uuid();
      dispatch({
        type: playerActions.PLAYER_LOAD_STATION,
        playSessionId,
        station,
      });

      if (autoPlay) {
        dispatch(playLiveAudio());
      }
    }
  };
}

export function play() {
  return async (dispatch, getState) => {
    const {
      player: { playbackMode },
    } = getState();

    switch (playbackMode) {
      case playbackModes.LIVE_AUDIO:
        dispatch(playLiveAudio());
        break;
      case playbackModes.LIVE_VIDEO:
        dispatch(playLiveVideo());
        break;
      case playbackModes.ON_DEMAND_CLIP:
        dispatch(playOnDemandClip());
        break;
      default:
        break;
    }
  };
}

export function stop(trackAnalytics = true) {
  return async (dispatch, getState) => {
    const {
      player: { playbackMode, isCasting },
    } = getState();

    if (isCasting) {
      ChromeSender.stop();
    } else {
      switch (playbackMode) {
        case playbackModes.LIVE_AUDIO:
          AudioPlayerStation.stop(trackAnalytics);
          huaweiPlayerEvents.message('stop');
          break;
        case playbackModes.LIVE_VIDEO:
          VideoJWPlayer.stop(trackAnalytics);
          break;
        case playbackModes.ON_DEMAND_CLIP:
          await AudioPlayerOnDemand.stop(trackAnalytics);
          huaweiPlayerEvents.message('stop');
          break;
        default:
          break;
      }
    }
    dispatch(playerStop());
  };
}

/**
 * loads station from query params or localStorage
 * also sets volume
 * if the station is present in url it will load that one instead of the one from localStorage
 * and will also play video if that is defined as type in url and video is available
 * @returns {Function}
 */
export function loadPlayItemWithParameters() {
  return async (dispatch, getState) => {
    const { player, urlParams } = getState();
    setVolumeLevel(player.volumeLevel, false);
    let stationParams = null;
    let mediaType = null;
    if ('station' in urlParams) {
      stationParams = urlParams.station;
      if ('type' in urlParams) {
        mediaType = urlParams.type;
      }
      const station = await graphql.fetchStation({ slug: stationParams });
      if (station) {
        if (mediaType === 'video' && 'videoUrl' in station) {
          dispatch(
            showNotification({
              message: NotificationTexts.PLAYBACK_LIVE_VIDEO_FROM_URL,
            })
          );
          dispatch(loadLiveVideo(station));
        } else {
          dispatch(loadStation(station, false));
        }
      }
    }
  };
}
